/**
 * What a Baseline model covers, shown above its Settings: the period type, which can be changed
 * with the figures carried across (summed into longer periods, put in the last period or
 * spread into shorter ones - IAS accounting's two choices); the filed span, set when the
 * project is made; and the forecast, one to five years after the last filed period.
 */

import { useState } from 'react';
import {
  MAX_FORECAST_YEARS,
  PERIODS_PER_YEAR,
  periodLabel,
  useMAccountingStore,
  type MComputed,
} from '../../../store/m-accounting';

const KIND = { annual: 'Annual', quarterly: 'Quarterly', monthly: 'Monthly' } as const;

export function MPeriods({ computed }: { computed: MComputed }) {
  const setForecastYears = useMAccountingStore((s) => s.setForecastYears);
  const setPeriodType = useMAccountingStore((s) => s.setPeriodType);
  const isSaving = useMAccountingStore((s) => s.isSaving);
  const layout = computed.layout;
  const [pending, setPending] = useState<keyof typeof KIND | null>(null);
  const [method, setMethod] = useState<'third_month' | 'equal_spread'>('third_month');
  if (!layout) return null;
  const wanted = pending ?? layout.periodType;
  const changing = wanted !== layout.periodType;
  const shorter = PERIODS_PER_YEAR[wanted] > PERIODS_PER_YEAR[layout.periodType];
  const perYear = PERIODS_PER_YEAR[layout.periodType];
  const years = Math.max(1, Math.round(layout.forecast / perYear));
  const first = computed.historical[0];
  const lastFiled = computed.historical[computed.historical.length - 1];
  return (
    <div className="mb-4 flex flex-wrap items-end gap-6 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm">
      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Periods</span>
        <select
          className="mt-0.5 block rounded border border-gray-300 px-2 py-1 text-sm"
          value={wanted}
          disabled={isSaving}
          onChange={(e) => setPending(e.target.value as keyof typeof KIND)}
          aria-label="Periods"
        >
          {(Object.keys(KIND) as (keyof typeof KIND)[]).map((k) => (
            <option key={k} value={k}>
              {KIND[k]}
            </option>
          ))}
        </select>
      </label>
      {changing && shorter && (
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Each year's amounts
          </span>
          <select
            className="mt-0.5 block rounded border border-gray-300 px-2 py-1 text-sm"
            value={method}
            disabled={isSaving}
            onChange={(e) => setMethod(e.target.value as typeof method)}
            aria-label="When splitting into shorter periods"
          >
            <option value="third_month">Put the whole amount in the last period</option>
            <option value="equal_spread">Spread it evenly</option>
          </select>
        </label>
      )}
      {changing && (
        <div className="flex items-end gap-2">
          <button
            type="button"
            className="rounded bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-40"
            disabled={isSaving}
            onClick={async () => {
              await setPeriodType(wanted, method);
              setPending(null);
            }}
          >
            Change to {KIND[wanted].toLowerCase()}
          </button>
          <button
            type="button"
            className="rounded px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            onClick={() => setPending(null)}
          >
            Cancel
          </button>
        </div>
      )}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Filed</div>
        <div className="mt-0.5 text-gray-900">
          {periodLabel(computed, first)} – {periodLabel(computed, lastFiled)} ({computed.historical.length})
        </div>
      </div>
      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Forecast years</span>
        <select
          className="mt-0.5 block rounded border border-gray-300 px-2 py-1 text-sm"
          value={years}
          disabled={isSaving}
          onChange={(e) => void setForecastYears(Number(e.target.value))}
          aria-label="Forecast years"
        >
          {Array.from({ length: MAX_FORECAST_YEARS }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n} year{n === 1 ? '' : 's'}
            </option>
          ))}
        </select>
      </label>
      <p className="max-w-md text-xs text-gray-500">
        {changing
          ? shorter
            ? 'Every figure moves with it: amounts into the periods of their year, balances moving as the amounts do, openings in the first. The current version is kept, so it can be restored.'
            : 'Every figure moves with it: amounts summed over each period, balances at its end, openings at its start. The current version is kept, so it can be restored.'
          : `After the last filed period, at most ${MAX_FORECAST_YEARS} years. Shortening it drops what was entered in the periods it removes.`}
      </p>
    </div>
  );
}
